import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MedicationIcon from '@mui/icons-material/Medication';
import PaymentsIcon from '@mui/icons-material/Payments';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { SMART_COLORS, smartCardSx } from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const MedicationsEdit = () => {
  const { t } = useTranslation();
  const { medicationId } = useParams();
  const navigate = useNavigate();
  const [medication, setMedication] = useState({
    medication_name: '',
    default_dosage: '',
    default_unit: '',
    default_duration: '',
    price: 0,
    pricing_mode: 'per_unit',
    stock_quantity: 0,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`${apiUrl}/medications/${medicationId}`)
      .then((response) => setMedication(response.data))
      .catch((fetchError) => {
        console.error('Error fetching medication data:', fetchError);
        setError(t('fetch_medication_error'));
      });
  }, [medicationId, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      await api.put(`${apiUrl}/medications/${medicationId}`, {
        ...medication,
        default_duration: medication.default_duration || null,
        price: medication.price || 0,
        stock_quantity: medication.stock_quantity || 0,
        modified_by: localStorage.getItem('userId'),
      });
      navigate('/medications');
    } catch (updateError) {
      console.error('Error updating medication:', updateError);
      setError(t('medication_update_error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="smart-page-container" sx={{ maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          ...smartCardSx,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              bgcolor: SMART_COLORS.blueSoft,
              color: SMART_COLORS.blue,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <MedicationIcon />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: SMART_COLORS.text }}>
              {t('edit_medication')}
            </Typography>
            <Typography sx={{ color: SMART_COLORS.muted }}>
              Update medication defaults, stock quantity, and accounting price.
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/medications')}
          sx={{ borderColor: SMART_COLORS.line, color: SMART_COLORS.text, fontWeight: 800 }}
        >
          {t('back', 'Back')}
        </Button>
      </Paper>

      <Paper elevation={0} sx={{ ...smartCardSx, p: { xs: 2.5, md: 3.5 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label={t('medication_name')}
              value={medication.medication_name || ''}
              onChange={(e) => setMedication({ ...medication, medication_name: e.target.value })}
              fullWidth
              required
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('default_dosage')}
                value={medication.default_dosage || ''}
                onChange={(e) => setMedication({ ...medication, default_dosage: e.target.value })}
                fullWidth
              />
              <TextField
                label={t('default_unit')}
                value={medication.default_unit || ''}
                onChange={(e) => setMedication({ ...medication, default_unit: e.target.value })}
                fullWidth
              />
              <TextField
                label={t('medicationsList.defaultDuration')}
                value={medication.default_duration || ''}
                onChange={(e) => setMedication({ ...medication, default_duration: e.target.value })}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label={t('price')}
                type="number"
                value={medication.price || ''}
                onChange={(e) => setMedication({ ...medication, price: e.target.value })}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PaymentsIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">SAR</InputAdornment>,
                }}
              />
              <TextField
                label={t('stock_quantity')}
                type="number"
                value={medication.stock_quantity || ''}
                onChange={(e) => setMedication({ ...medication, stock_quantity: e.target.value })}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Inventory2Icon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <FormControl fullWidth>
              <InputLabel>{t('pricingMode.label', 'نمط التسعير')}</InputLabel>
              <Select
                label={t('pricingMode.label', 'نمط التسعير')}
                value={medication.pricing_mode || 'per_unit'}
                onChange={(e) => setMedication({ ...medication, pricing_mode: e.target.value })}
              >
                <MenuItem value="per_unit">{t('pricingMode.perUnit', 'بالتجزئة (سعر العلبة ÷ حجمها)')}</MenuItem>
                <MenuItem value="flat">{t('pricingMode.flat', 'كامل غير قابل للتجزئة (مثل المغذّي)')}</MenuItem>
              </Select>
              <Typography variant="caption" sx={{ color: SMART_COLORS.muted, mt: 0.5 }}>
                {t('pricingMode.hint', 'اختر "كامل غير قابل للتجزئة" للعناصر التي تُصرف كوحدة واحدة كاملة بصرف النظر عن الجرعة.')}
              </Typography>
            </FormControl>
            <Divider />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/medications')}
                sx={{ borderColor: SMART_COLORS.line, color: SMART_COLORS.text, fontWeight: 800 }}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={saving}
                startIcon={<SaveIcon />}
                sx={{ bgcolor: SMART_COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: SMART_COLORS.goldHover } }}
              >
                {t('save')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default MedicationsEdit;
